import React, { useEffect, useState, createContext, useContext, useCallback } from 'react';
import { useRouter } from 'next/router';
import * as actionTypes from '@redux/actionTypes';
import { useDispatch } from 'react-redux';
import { getModeByUrl, WALLET_ADAPTERS } from '@util/config';
import Script from 'next/script';
import { createAccountToken, getUserDetails, getPenalties } from '@services/api';
import { safeLocalStorage } from '@utils/localstorage';
import { setDiscordConnected, setBackendConnected } from '@redux/slices/appConnectionsSlice';
import { setDiscordInfo, setBackendInfo } from '@redux/slices/appUserSlice';
import { createChainConfig, createPrivateKeyProvider } from '../config/solanaConfig';
import { createWeb3Auth, createOpenloginAdapter } from '../config/web3AuthConfig';
import { MY_DOMAIN_WHITE_LIST, DRIVE_DOMAIN_WHITE_LIST } from '@config/routerConfig';
import { getUserGamePasses } from '@services/user';
import { extractSubdomain } from '@utils/subdomain';
import GamePassImage from '@images/game-pass/license-image.png';
import EmailValidationModal from '@components/Modal/EmailValidationModal';
import { initDebugCommands } from '@utils/debug';

const isBrowser = typeof window !== 'undefined';

const AuthProviderContext = createContext();

const useAuth = () => useContext(AuthProviderContext);

const AuthProvider = ({ children }) => {
	const dispatch = useDispatch();
	const router = useRouter();
	const userLocalStoargedata = safeLocalStorage.getItem('token');
	const [user, setUser] = useState(null);
	const [walletAddress, setWalletAddress] = useState('');
	const [userDetails, setUserDetails] = useState(null);
	const [client, setClient] = useState(null);
	const [noBotInfoPopUp, setNoBotInfoPopUp] = useState(false);
	const [fullScreenSize, setFullScreenSize] = useState(false);
	const [userPlayRedirect, setUserPlayRedirect] = useState(false);
	const [apiLoading, setApiLoading] = useState(true);
	const [showEmailValidationModal, setShowEmailValidationModal] = useState(false);

	const [web3auth, setWeb3auth] = useState(null);
	const [provider, setProvider] = useState(null);
	const [loggedIn, setLoggedIn] = useState(false);
	const [loginFlag, setLoginFlag] = useState(false);
	// True once web3auth.init() has resolved (success or failure). Pages that
	// gate their UI on auth state (e.g. Main) need this to avoid rendering a
	// "logged out" view during the async init — without it, the Landing page
	// briefly flashes after a Discord OAuth redirect-back, before the auth
	// state catches up.
	const [authInitialized, setAuthInitialized] = useState(false);

	useEffect(() => {
		init();
		if (userLocalStoargedata) {
			setUserDetail();
			getPenaltiesData();
			getGamePasses();
		}
		console.log('when refreshed all loading');
		
		// Initialize debug commands in development
		if (process.env.NODE_ENV === 'development') {
			initDebugCommands();
		}
	}, []);

	const init = useCallback(async () => {
		try {
			const chainConfig = createChainConfig();
			const privateKeyProvider = createPrivateKeyProvider(chainConfig);
			const web3auth = createWeb3Auth(chainConfig, privateKeyProvider);
			const authAdapter = createOpenloginAdapter(privateKeyProvider);

			setWeb3auth(web3auth);

			web3auth.configureAdapter(authAdapter);

			await web3auth.init();
			setProvider(web3auth.provider);
			if (web3auth.status === 'ready') {
				setLoggedIn(false);
			}
			// Session already in localStorage — setUserDetail refreshes it; avoid duplicate setAccountInfo flow.
			if ((web3auth.connected || web3auth.status === 'connected') && !userLocalStoargedata) {
				setLoginFlag(true);
			}
		} catch (error) {
			console.error(error);
			setLoggedIn(false);
		} finally {
			// Always flip — pages waiting on this should unblock even on error.
			setAuthInitialized(true);
		}
	}, []);

	const login = useCallback(async () => {
		if (!web3auth) {
			return;
		}
		const web3authProvider = await web3auth.connectTo(WALLET_ADAPTERS.AUTH, {
			loginProvider: 'discord'
		});
		setProvider(web3authProvider);
	}, [web3auth]);

	const web3AuthLogout = useCallback(async () => {
		// Reset auth flags synchronously up-front so the UI doesn't stay stuck
		// on a spinner while web3auth.logout() resolves (it can be slow), and
		// so a missing/uninitialized web3auth doesn't leave us in a "logged in"
		// state forever.
		setLoggedIn(false);
		setLoginFlag(false);
		setProvider(null);
		dispatch(setDiscordConnected(false));
		dispatch(setBackendConnected(false));

		if (!web3auth) return;
		try {
			await web3auth.logout();
		} catch (e) {
			console.warn('web3auth.logout failed:', e);
		}
	}, [web3auth]);

	const redirectAuthenticatedUser = useCallback((user) => {
		if (!user) return;
		if (user.blocklisted) {
			router.push('/banned');
			return;
		}

		const joinToken = safeLocalStorage.getItem('joinToken');
		const subdomain = extractSubdomain();
		const currentRoute = router.pathname.split('/')[1] || '';

		if (joinToken) {
			router.push(`/join/${joinToken}`);
			return;
		}
		if (getModeByUrl() === 'testdrive') {
			router.push(user.has_test_drive ? '/dashboard' : '/testdrive');
			return;
		}
		// Prod web3auth returns the Discord callback to /activate (mode 'activate'),
		// not /activate-login — only /activate is an accepted redirect for the prod
		// client. Handle both so login completes wherever the callback lands.
		if (getModeByUrl() === 'activate-login' || getModeByUrl() === 'activate') {
			router.push('/activate-account');
			return;
		}
		if (getModeByUrl() === 'dashboard-login') {
			router.push('/owner/dashboard');
			return;
		}
		if (subdomain === 'my' && !MY_DOMAIN_WHITE_LIST.includes(currentRoute)) {
			router.push('/owner/dashboard');
			return;
		}
		if (subdomain === 'drive' && !DRIVE_DOMAIN_WHITE_LIST.includes(currentRoute)) {
			router.push(user.has_test_drive ? '/dashboard' : '/testdrive');
			return;
		}
		// localhost `/` and other entry URLs — previously no redirect, spinner never cleared
		if (router.pathname === '/' || router.pathname === '') {
			router.replace(user.has_test_drive ? '/dashboard' : '/testdrive');
		}
	}, [router]);

	const setAccountInfo = useCallback(async (walletAddress) => {
		console.log('setAccountInfo----------------');
		const walletName = 'torus';
		if (!walletAddress || !walletName || !web3auth) {
			// Nothing to process — clear the post-callback spinner (gated on
			// loginFlag) so we don't leave a stuck gray overlay.
			setLoginFlag(false);
			return;
		}

		setLoggedIn(true);
		// Any terminal path that does NOT redirect must clear loginFlag, else the
		// spinner/gray overlay stays up forever (loginFlag is only reset on logout).
		let navigated = false;
		try {
			let userInfo;
			try {
				userInfo = await web3auth.getUserInfo();
			} catch (e) {
				console.error('setAccountInfo: getUserInfo failed', e);
				return;
			}

			if (!userInfo?.idToken) {
				await web3AuthLogout();
				return;
			}

			if (!userInfo?.email) {
				setShowEmailValidationModal(true);
				await web3AuthLogout();
				return;
			}

			dispatch(
				setDiscordInfo({
					name: userInfo?.name,
					email: userInfo?.email,
					profileImage: userInfo?.profileImage,
					verifierId: userInfo?.verifierId,
					verifier: userInfo?.verifier
				})
			);

			safeLocalStorage.setItem('discordDetails', userInfo);
			safeLocalStorage.setItem('typeOfLogin', userInfo?.typeOfLogin);
			const user = {
				user: {
					wallet_address: walletAddress,
					provider: walletName,
					username: userInfo?.name,
					email: userInfo?.email,
					discord_id: userInfo?.verifierId
				},
				id_token: userInfo?.idToken
			};

			const loginData = await createAccountToken(user, userInfo?.verifierId);
			if (!loginData?.data) {
				if (isBrowser && window.Intercom) {
					window.Intercom('shutdown');
				}
				return;
			}

			safeLocalStorage.setItem('id_Token', userInfo?.idToken);
			safeLocalStorage.setItem('authToken', loginData?.data?.token);
			dispatch({
				type: actionTypes.SET_USER,
				payload: loginData?.data.user
			});

			if (isBrowser && window?.gtag) {
				window.gtag('config', 'G-XXFJFZBQM2', {
					user_id: loginData?.data?.user?.id
				});
				window.gtag('event', 'user_login', {
					user_id: loginData?.data?.user?.id
				});
			}

			if (isBrowser && window.Intercom) {
				window.Intercom('boot', {
					app_id: 'd8g07kkh',
					name: loginData?.data?.user?.username,
					user_id: loginData?.data?.user?.id,
					email: loginData?.data?.user?.email,
					intercom_user_jwt: loginData?.data?.intercom_user_jwt
				});
			}

			setUserDetails(loginData?.data.user);
			if (loginData.data.token) {
				redirectAuthenticatedUser(loginData?.data?.user);
				navigated = true;
			}
			return loginData.data;
		} catch (error) {
			console.log('error: ', error);
		} finally {
			setLoggedIn(false);
			// If we didn't redirect (failure / early return), drop the spinner so
			// the user returns to the login screen and can retry.
			if (!navigated) setLoginFlag(false);
		}
	}, [web3auth, dispatch, redirectAuthenticatedUser, web3AuthLogout]);

	const setUserDetail = useCallback(async () => {
		try {
			const response = await getUserDetails();
			if (response?.data?.user) {
				const token = safeLocalStorage.getItem('token');
				token.user = response?.data?.user;
				safeLocalStorage.setItem('token', token);

				dispatch({
					type: actionTypes.SET_USER,
					payload: response?.data.user
				});

				const pathname = router.pathname.split('/')[1];

				// Set Backend Connected
				dispatch(setBackendConnected(true));

				// Set backend user info
				dispatch(
					setBackendInfo({
						...response?.data?.user,
						id: response?.data?.user?.id,
						username: response?.data?.user?.username,
						walletAddress: response?.data?.user?.wallet_address,
						timeCredit: response?.data?.user?.time_credit,
						kycId: response?.data?.user?.kyc_id,
						kycStatus: response?.data?.user?.kyc_status,
						kycReference: response?.data?.user?.kyc_reference
					})
				);

				setUserDetails(response?.data.user);
				if (isBrowser && window?.gtag) {
					window.gtag('config', 'G-XXFJFZBQM2', {
						user_id: response?.data?.user?.id
					});
					window.gtag('event', `user_change_page_${pathname}`, {
						user_id: response?.data?.user?.id
					});
				}
				if (isBrowser && window.Intercom) {
					console.log('intercom window detected!!');
					console.log('response?.data?.user?.intercom_user_jwt', response?.data);
					window.Intercom('update', {
						user_id: response?.data?.user?.id,
						name: response?.data?.user?.username,
						email: response?.data?.user?.email,
						intercom_user_jwt: response?.data?.user?.intercom_user_jwt
					});
				}
				if (response?.data?.user?.blocklisted) {
					router.push('/banned');
				} else if (router.pathname === '/' || router.pathname === '') {
					redirectAuthenticatedUser(response?.data?.user);
				}
				setTimeout(() => {
					dispatch({
						type: actionTypes.LOADING_SPINNER,
						payload: false
					});
				}, 1000);
			} else if (response?.status === 401 && response?.data?.errors) {
				// Only logout on explicit 401 authentication failure from the backend.
				// Backend returns { errors: "Invalid or expired token" } with status 401.
				logOut();
			}
			// For network errors, timeouts, 500s, or unexpected response shapes:
			// do NOT logout. The user's local token may still be valid.
			// Silently fail and let the user continue with their cached session.
		} catch (error) {
			console.log('error: ', error);
			// Do not logout on exceptions — these are typically network/transient issues.
			// The user's stored JWT may still be perfectly valid.
		}
	}, [router, redirectAuthenticatedUser]);

	const logOut = useCallback(() => {
		web3AuthLogout();
		dispatch({
			type: actionTypes.CLEAR_REDUX,
			payload: {}
		});
		const tournamentData = safeLocalStorage.getItem('tournamentData')
		safeLocalStorage.clear();
		sessionStorage.clear();
		safeLocalStorage.setItem('openlogin_store', {});
		if (tournamentData && tournamentData?.deviceId) {
			safeLocalStorage.setItem('tournamentData', tournamentData);
		}

		if (getModeByUrl() === 'testdrive') {
			router.push('/testdrive');
		} else if (getModeByUrl() === 'dashboard-login') {
			router.push('/dashboard-login');
		} else if (getModeByUrl() === 'activate-login') {
			router.push('/activate-login');
		} else {
			router.push("/");
		}
	}, [web3AuthLogout, dispatch]);

	const getGamePasses = async () => {
		try {
			const response = await getUserGamePasses();
			if (response) {
				const { latest_minting: statusOfPass, licenseImage: _, ...rest } = response;

				const fullLicenceMetaData = {
					statusOfPass,
					licenseImage: statusOfPass === 'minting' ? GamePassImage : _,
					nft_ids: response.nft_ids,
					...rest
				};
				fullLicenceMetaData.statusOfPass = fullLicenceMetaData.statusOfPass || response.nft_ids?.length > 0

				dispatch({
					type: actionTypes.FULL_LICENCE_META_DATA,
					payload: {
						fullLicenceMetaData
					}
				});
				setApiLoading(false)
			}
		} catch (error) {
			setApiLoading(false)
			console.log('error: ', error);
		}
	}

	const getPenaltiesData = useCallback(async () => {
		const penaltiesData = await getPenalties()
		if (penaltiesData?.penalties?.length > 0) {
			const penalties = penaltiesData.penalties.filter((i) => i?.appeal?.status !== 'confirmed');
			dispatch({
				type: actionTypes.PENALTYDATA,
				payload: penalties
			});
			const minorPenalties = penaltiesData.penalties.filter((i) => i.penalty_reason.severity === 'minor');
			if (minorPenalties.length > 0) {
				dispatch({
					type: actionTypes.MINOR_PENALTY,
					payload: minorPenalties
				});
			}
			const majorPenalties = penaltiesData.penalties.filter((i) => i.penalty_reason.severity === 'major');
			if (majorPenalties.length > 0) {
				dispatch({
					type: actionTypes.MAJOR_PENALTY,
					payload: majorPenalties
				});
			}
		}
	}, []);

	return (
		<AuthProviderContext.Provider
			value={{
				web3auth,
				provider,
				user,
				login,
				web3AuthLogout,
				loggedIn,
				setUser,
				setProvider,
				walletAddress,
				setWalletAddress,
				userDetails,
				setUserDetails,
				client,
				setClient,
				noBotInfoPopUp,
				setNoBotInfoPopUp,
				fullScreenSize,
				setFullScreenSize,
				userPlayRedirect,
				setUserPlayRedirect,
				setAccountInfo,
				loginFlag,
				authInitialized,
				apiLoading
			}}
		>
			<Script
				src="https://www.googletagmanager.com/gtag/js?id=G-XXFJFZBQM2"
				strategy="afterInteractive"
			/>
			<Script
				id="gtag-init"
				strategy="afterInteractive"
				dangerouslySetInnerHTML={{
					__html: `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', 'G-XXFJFZBQM2');
`
				}}
			/>
			{children}
			<EmailValidationModal 
				isOpen={showEmailValidationModal} 
				onClose={() => setShowEmailValidationModal(false)} 
			/>
		</AuthProviderContext.Provider>
	);
};

export { useAuth, AuthProvider };
